import { useCallback } from "react";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, CheckCircle2, Truck, Container, ClipboardCheck, Users, FileText } from "lucide-react";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { StarOrderButton } from "@/components/orders/StarOrderButton";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { DataCard } from "@/components/ui/data-card";
import { InlineEditCell } from "@/components/orders/InlineEditCell";
import { InlineAssignCell } from "@/components/orders/InlineAssignCell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { formatTimeWithTz, formatWindowCompact } from "@/lib/order-time";
import type { Order, Customer, Tractor, Driver } from "@/types/tms";
import { DashboardPagination, usePagination } from "@/components/dashboard/DashboardPagination";
import { useOrderLegSummaries } from "@/hooks/useOrderLegSummaries";

export type LateOrder = Order & {
  billing_party: Customer | null;
  assigned_tractor: Tractor | null;
  assigned_operator: Driver | null;
  assigned_operator_2: Driver | null;
};

interface LateOrdersTableProps {
  title: string;
  /** Which time-of-interest to highlight in warning style. */
  variant: "departure" | "arrival" | "stale" | "manual-review";
  orders: LateOrder[] | undefined;
  onRowClick: (order: LateOrder) => void;
  selectedId?: string | null;
  /** Force a fixed column count for the card grid. Defaults to responsive 1/2/3. */
  columns?: 1;
  /** Optional secondary line rendered under the title. */
  subtitle?: string;
  /** When true, render an Operators column / row showing assigned operators. */
  showOperators?: boolean;
  /** Optional row action — when provided, renders a "Mark Delivered" button
   *  that shows on row hover (desktop) or always (mobile cards). */
  onMarkDelivered?: (order: LateOrder) => void;
  /** Id of the order whose mark-delivered action is in flight. */
  markDeliveredPendingId?: string | null;
  /** When true and orders is empty, replace the table + empty card with a single-line
   *  "No <title>" header at viewports below `lg`. Desktop is unchanged. */
  compactEmpty?: boolean;
}

const WARNING_TEXT = "text-[hsl(var(--aurora-status-warning))]";

function ResourceChip({
  icon: Icon,
  value,
}: {
  icon: typeof Truck;
  value: string;
}) {
  return (
    <Badge variant="outline" className="gap-1 font-normal">
      <Icon className="h-3 w-3" />
      <span className="truncate max-w-[140px]">{value}</span>
    </Badge>
  );
}

interface LateOrderCardProps {
  order: LateOrder;
  variant: "departure" | "arrival" | "stale" | "manual-review";
  selected: boolean;
  onClick: () => void;
  showOperators?: boolean;
  onMarkDelivered?: (order: LateOrder) => void;
  markDeliveredPending?: boolean;
}

function LateOrderCard({ order, variant, selected, onClick, showOperators, onMarkDelivered, markDeliveredPending, legs }: LateOrderCardProps & { legs?: Order["legs"] }) {
  const viewerTz = useViewerTimezone();
  const lateReady = variant === "departure";
  const lateArr = variant === "arrival";
  const { isReadOnly } = useUserRole();
  const isTerminalStatus = order.status === "in_transit" || order.status === "delivered" || order.status === "canceled";
  const isTerminal = isTerminalStatus || isReadOnly;
  const readyTxt = formatWindowCompact(
    order.ready_time,
    order.ready_time_end,
    viewerTz,
    "MMM d",
    "HH:mm",
    { withTz: true },
  );
  const hasEta = order.status === "in_transit" && (order as any).eapi_eta;
  const deliveryTxt = hasEta
    ? `ETA: ${formatTimeWithTz((order as any).eapi_eta, viewerTz, "MMM d, HH:mm")}`
    : order.requested_delivery_time
      ? formatWindowCompact(
          order.requested_delivery_time,
          order.requested_delivery_time_end,
          viewerTz,
          "MMM d",
          "HH:mm",
          { withTz: true },
        )
      : "—";

  const operatorNames = [order.assigned_operator?.name, order.assigned_operator_2?.name]
    .filter(Boolean)
    .join(", ");

  return (
    <DataCard selected={selected} onClick={onClick}>
      {/* Header — star, order ID, optional action */}
      <DataCard.Header>
        <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center">
          <StarOrderButton orderId={order.id} size="sm" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="font-mono font-bold text-lg leading-none text-foreground truncate">
            {order.unique_id}
          </span>
        </div>
        {onMarkDelivered ? (
          <span onClick={(e) => e.stopPropagation()} className="inline-flex">
            <Button
              size="sm"
              variant="outline"
              disabled={markDeliveredPending}
              onClick={(e) => {
                e.stopPropagation();
                onMarkDelivered(order);
              }}
            >
              {markDeliveredPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Mark Delivered
            </Button>
          </span>
        ) : null}
      </DataCard.Header>
      {order.billing_party?.organization_name && (
        <div className="text-sm text-muted-foreground truncate [font-variant-caps:all-small-caps] -mt-1">
          {order.billing_party.organization_name}
        </div>
      )}

      <DataCard.Chips>
        <OrderStatusBadge status={order.status} late legs={legs} />
        {order.trip_type ? (
          <Badge variant="outline" className="font-normal">{order.trip_type}</Badge>
        ) : null}
      </DataCard.Chips>

      <DataCard.Body>
        <DataCard.Field
          icon={ArrowUpRight}
          tone={lateReady ? "warning" : "default"}
          secondary={readyTxt}
        >
          {order.ship_from_city?.replace(/[,\s]+$/, "") ?? "—"}
        </DataCard.Field>
        <DataCard.Field
          icon={ArrowDownRight}
          tone={lateArr ? "warning" : "default"}
          secondary={deliveryTxt}
        >
          {order.ship_to_city?.replace(/[,\s]+$/, "") ?? "—"}
        </DataCard.Field>
      </DataCard.Body>

      {/* Stacked tiles — mirrors the Orders page card */}
      <div className="flex flex-col gap-2">
        <DataCard.Tile
          icon={Truck}
          label="Truck ID"
          value={
            order.assigned_tractor?.name ? (
              order.assigned_tractor.name
            ) : (
              <span className="text-muted-foreground font-normal">No truck assigned</span>
            )
          }
          trailing={
            order.assigned_tractor?.name || isTerminal ? undefined : (
              <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                <InlineAssignCell
                  orderId={order.id}
                  kind="tractor"
                  currentId={(order as any).assigned_tractor_id ?? null}
                  currentName={order.assigned_tractor?.name ?? null}
                  readOnly={isTerminal}
                />
              </span>
            )
          }
        />
        <DataCard.Tile
          icon={Container}
          label="Trailer ID"
          value={
            order.trailer_id ? (
              order.trailer_id
            ) : (
              <span className="text-muted-foreground font-normal">No trailer assigned</span>
            )
          }
          trailing={
            order.trailer_id || isTerminal ? undefined : (
              <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                <InlineEditCell
                  orderId={order.id}
                  fieldName="trailer_id"
                  currentValue={order.trailer_id}
                  placeholder="Trailer ID"
                  orderTrailerTypeId={order.trailer_type_id}
                  orderShipFromCity={order.ship_from_city}
                  readOnly={isTerminal}
                />
              </span>
            )
          }
        />
        <DataCard.Tile
          icon={Users}
          label="Operator name(s)"
          value={
            operatorNames ? (
              operatorNames
            ) : (
              <span className="text-muted-foreground font-normal">No operators assigned</span>
            )
          }
          trailing={
            operatorNames || isTerminal ? undefined : (
              <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                <InlineAssignCell
                  orderId={order.id}
                  kind="operator"
                  currentId={(order as any).assigned_operator_id ?? null}
                  currentName={order.assigned_operator?.name ?? null}
                  readOnly={isTerminal}
                />
              </span>
            )
          }
        />
        <DataCard.Tile
          icon={FileText}
          label="Documents"
          value="Documents"
          trailing={
            <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <BolViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
              <TenderViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
            </span>
          }
        />
        {order.tender_id ? (
          <DataCard.Tile icon={FileText} label="Tender ID" value={order.tender_id} />
        ) : null}
      </div>
    </DataCard>
  );
}

export function LateOrdersBody({
  variant,
  orders,
  onRowClick,
  selectedId,
  columns,
  emptyText,
  showOperators,
  onMarkDelivered,
  markDeliveredPendingId,
}: {
  variant: "departure" | "arrival" | "stale" | "manual-review";
  orders: LateOrder[] | undefined;
  onRowClick: (order: LateOrder) => void;
  selectedId?: string | null;
  columns?: 1;
  emptyText?: string;
  showOperators?: boolean;
  onMarkDelivered?: (order: LateOrder) => void;
  markDeliveredPendingId?: string | null;
}) {
  const viewerTz = useViewerTimezone();
  const { isReadOnly } = useUserRole();
  const handleClick = useCallback((o: LateOrder) => onRowClick(o), [onRowClick]);

  const { page, setPage, totalPages, pageRows, rangeStart, rangeEnd, total } = usePagination(orders);

  // One batched leg-status lookup for the rows on screen (never per row), so
  // multi-leg orders can show the leg-derived rollup chip.
  const { legsFor } = useOrderLegSummaries(pageRows.map((o) => o.id));

  if (!orders || orders.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-10 text-center min-h-[160px]">
        <CheckCircle2 className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">{emptyText ?? "Nothing to show"}</p>
      </div>
    );
  }

  const unifiedCards = (
    <div className="grid grid-cols-1 gap-3">
      {pageRows.map((o) => (
        <LateOrderCard
          key={o.id}
          order={o}
          variant={variant}
          selected={selectedId === o.id}
          onClick={() => handleClick(o)}
          showOperators={showOperators}
          onMarkDelivered={onMarkDelivered}
          markDeliveredPending={markDeliveredPendingId === o.id}
          legs={legsFor(o.id)}
        />
      ))}
    </div>
  );

  const tableNode = (
    <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Ship From</TableHead>
              <TableHead>Ship To</TableHead>
              <TableHead>Trailer ID</TableHead>
              <TableHead>Tractor</TableHead>
              {showOperators ? <TableHead>Operators</TableHead> : null}
              {onMarkDelivered ? <TableHead className="w-[160px]" /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((o) => {
              const lateReady = variant === "departure";
              const lateArr = variant === "arrival";
              const isTerminalStatus = o.status === "in_transit" || o.status === "delivered" || o.status === "canceled";
              const isTerminal = isTerminalStatus || isReadOnly;
              const readyTxt = formatWindowCompact(
                o.ready_time,
                o.ready_time_end,
                viewerTz,
                "MMM d",
                "HH:mm",
                { withTz: true },
              );
              const hasEta = o.status === "in_transit" && (o as any).eapi_eta;
              const deliveryTxt = hasEta
                ? `ETA: ${formatTimeWithTz((o as any).eapi_eta, viewerTz, "MMM d, HH:mm")}`
                : o.requested_delivery_time
                  ? formatWindowCompact(
                      o.requested_delivery_time,
                      o.requested_delivery_time_end,
                      viewerTz,
                      "MMM d",
                      "HH:mm",
                      { withTz: true },
                    )
                  : "—";
              const selected = selectedId === o.id;
              return (
                <TableRow
                  key={o.id}
                  onClick={() => handleClick(o)}
                  className={cn("cursor-pointer group", selected && "bg-accent")}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                        <StarOrderButton orderId={o.id} size="sm" />
                      </span>
                      <span className="font-mono font-semibold whitespace-nowrap">{o.unique_id}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <ArrowUpRight className="h-4 w-4 shrink-0" />
                      <span className="truncate">{o.ship_from_city?.replace(/[,\s]+$/, "") ?? "—"}</span>
                    </div>
                    <div className={cn("text-xs mt-0.5", lateReady ? WARNING_TEXT : "text-muted-foreground")}>
                      {readyTxt}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <ArrowDownRight className="h-4 w-4 shrink-0" />
                      <span className="truncate">{o.ship_to_city?.replace(/[,\s]+$/, "") ?? "—"}</span>
                    </div>
                    <div className={cn("text-xs mt-0.5", lateArr ? WARNING_TEXT : "text-muted-foreground")}>
                      {deliveryTxt}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                      <InlineEditCell
                        orderId={o.id}
                        fieldName="trailer_id"
                        currentValue={o.trailer_id}
                        placeholder="Trailer ID"
                        orderTrailerTypeId={o.trailer_type_id}
                        orderShipFromCity={o.ship_from_city}
                        readOnly={isTerminal}
                      />
                    </span>
                  </TableCell>
                  <TableCell>
                    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                      <InlineAssignCell
                        orderId={o.id}
                        kind="tractor"
                        currentId={(o as any).assigned_tractor_id ?? null}
                        currentName={o.assigned_tractor?.name ?? null}
                        readOnly={isTerminal}
                      />
                    </span>
                  </TableCell>
                  {showOperators ? (
                    <TableCell>
                      <span className="text-sm truncate">
                        {[o.assigned_operator?.name, o.assigned_operator_2?.name]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </span>
                    </TableCell>
                  ) : null}
                  {onMarkDelivered ? (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={markDeliveredPendingId === o.id}
                        className={cn(
                          "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity",
                          markDeliveredPendingId === o.id && "opacity-100",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkDelivered(o);
                        }}
                      >
                        {markDeliveredPendingId === o.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Mark Delivered
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
  );

  return (
    <>
      <ResponsiveTableShell cards={unifiedCards} table={tableNode} />
      <DashboardPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        total={total}
      />
    </>
  );
}

const EMPTY_TEXT_BY_VARIANT: Record<"departure" | "arrival" | "stale" | "manual-review", string> = {
  departure: "No late departures today",
  arrival: "No late arrivals today",
  stale: "No stale orders",
  "manual-review": "No manual trips awaiting review",
};

export function LateOrdersTable({ title, variant, orders, onRowClick, selectedId, columns, subtitle, showOperators, compactEmpty }: LateOrdersTableProps) {
  const handleClick = useCallback(
    (o: LateOrder) => onRowClick(o),
    [onRowClick],
  );

  const count = orders?.length ?? 0;
  const isEmpty = count === 0;
  const compactEmptyHeader = compactEmpty && isEmpty;
  const lowerTitle = title.toLowerCase();

  return (
    <Card className="shrink-0 h-full flex flex-col">
      <CardContent className="p-6 flex-1 flex flex-col">
        <div className={cn(compactEmptyHeader ? "lg:mb-4" : "mb-4")}>
          {/* Mobile/tablet header */}
          <div className={cn("flex items-center gap-2", compactEmptyHeader ? "lg:hidden" : "hidden")}>
            {variant === "departure" ? (
              <ArrowUpRight className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            ) : variant === "arrival" ? (
              <ArrowDownRight className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            ) : variant === "manual-review" ? (
              <ClipboardCheck className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            )}
            <span className="font-medium text-lg">No {lowerTitle}</span>
          </div>
          {/* Desktop header (and mobile when not compactEmpty) */}
          <div className={cn("flex items-center gap-2", compactEmptyHeader && "hidden lg:flex")}>
            {variant === "departure" ? (
              <ArrowUpRight className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            ) : variant === "arrival" ? (
              <ArrowDownRight className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            ) : variant === "manual-review" ? (
              <ClipboardCheck className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
            )}
            <span className="font-medium text-lg">{title}</span>
            <span className="text-sm text-muted-foreground">({count})</span>
          </div>
          {subtitle ? (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          ) : null}
        </div>
        <div className={cn("flex-1 flex flex-col", compactEmptyHeader && "hidden lg:flex")}>
          <LateOrdersBody
            variant={variant}
            orders={orders}
            onRowClick={handleClick}
            selectedId={selectedId}
            columns={columns}
            emptyText={EMPTY_TEXT_BY_VARIANT[variant]}
            showOperators={showOperators}
          />
        </div>
      </CardContent>
    </Card>
  );
}
